import { useAtomValue } from "jotai";
import { type FunctionComponent, type ReactNode, type Ref } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { rosettaAtom } from "scmp-app/lib/rosetta";
import { useNewHomepageSurveyContext } from "scmp-app/pages/home/contexts";
import type { topStoriesHomeV2QueueItemsEdge$key } from "scmp-app/queries/__generated__/topStoriesHomeV2QueueItemsEdge.graphql";

import {
  sendTopStoriesClickTracking,
  useTopStoriesImpressionTracking,
  useTopStoriesNativeAdImpressionTracking,
} from "./hooks";
import {
  AdSlotContainer,
  Container,
  HorizontalDivider,
  InlineAds,
  PlusWidget,
  StyledTopStoriesItem,
  TopStory1,
  TopStory10,
  TopStory11,
  TopStory12,
  TopStory13,
  TopStory14,
  TopStory15,
  TopStory2,
  TopStory3,
  TopStory4,
  TopStory5,
  TopStory6,
  TopStory7,
  TopStory8,
  TopStory9,
  VerticalDivider,
} from "./styles";

const topStoriesArticleCount = 15;

type Props = {
  className?: string;
  reference: topStoriesHomeV2QueueItemsEdge$key;
  slots?: {
    mobileInlineAds?: ReactNode;
    nativeAds?: ReactNode;
    plusWidget?: ReactNode;
    sponsorArticle?: (
      tracking: typeof sendTopStoriesClickTracking,
      ref?: Ref<HTMLDivElement>,
    ) => ReactNode;
  };
};

export const HomeV2TopStories: FunctionComponent<Props> = ({
  className,
  reference,
  slots = {},
}) => {
  const edges = useFragment(
    graphql`
      fragment topStoriesHomeV2QueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            entityId
            ...homeSecondaryV2ContentItemContent
            ...topStoriesItemV2Content
          }
        }
      }
    `,
    reference,
  );

  const nodes = (edges ?? [])
    .map(edge => edge?.node)
    .filter(notEmpty)
    .slice(0, topStoriesArticleCount);
  const listOfArticles = nodes.map(node => node.entityId ?? "");

  const { triggerElement: triggerNewHomepageSurveyElement } = useNewHomepageSurveyContext();
  const captureFirstNativeAdImpressionEvent = useTopStoriesNativeAdImpressionTracking();
  const {
    captureTrackImpressionEventTargetFirstSetElement,
    captureTrackImpressionEventTargetFourthSetElement,
    captureTrackImpressionEventTargetSecondSetElement,
    captureTrackImpressionEventTargetThirdSetElement,
  } = useTopStoriesImpressionTracking(listOfArticles);

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;

  const topStoryComponents = [
    TopStory1,
    TopStory2,
    TopStory3,
    TopStory4,
    TopStory5,
    TopStory6,
    TopStory7,
    TopStory8,
    TopStory9,
    TopStory10,
    TopStory11,
    TopStory12,
    TopStory13,
    TopStory14,
    TopStory15,
  ];

  if (!notEmpty(nodes)) return null;
  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.TopStory,
      }}
    >
      <Container $hasScmpPlusAccessRight={hasScmpPlusAccessRight} className={className}>
        {slots.mobileInlineAds && <InlineAds>{slots.mobileInlineAds}</InlineAds>}
        {slots.plusWidget && hasScmpPlusAccessRight && <PlusWidget>{slots.plusWidget}</PlusWidget>}

        {topStoryComponents.map((Component, index) => {
          const impressionTrackingFunction = (() => {
            switch (index) {
              case 0:
                return captureTrackImpressionEventTargetFirstSetElement;
              case 4:
                return captureTrackImpressionEventTargetSecondSetElement;
              case 9:
                return captureTrackImpressionEventTargetThirdSetElement;
              case 12:
                return captureTrackImpressionEventTargetFourthSetElement;
            }
          })();

          return (
            <Component key={index}>
              <StyledTopStoriesItem
                isShowImage
                onClick={(entityId, urlAlias, isSponsorArticle) => {
                  sendTopStoriesClickTracking({
                    entityId,
                    index,
                    isSponsorArticle,
                    urlAlias,
                  });
                }}
                ref={impressionTrackingFunction}
                reference={nodes[index]}
                withCarousel
                withExclusiveHeaderFlag
                withSeriesShortLabel
                withSummary
              />
              {index === 3 && slots.sponsorArticle && (
                <>
                  <VerticalDivider />
                  <HorizontalDivider />
                  {slots.sponsorArticle(
                    sendTopStoriesClickTracking,
                    captureFirstNativeAdImpressionEvent,
                  )}
                </>
              )}
              {index === 14 && slots.nativeAds && (
                <>
                  <VerticalDivider />
                  <HorizontalDivider />
                  <AdSlotContainer>{slots.nativeAds}</AdSlotContainer>
                </>
              )}
              {index === 7 && <VerticalDivider />}
              {index === 11 && triggerNewHomepageSurveyElement}
            </Component>
          );
        })}
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2TopStories.displayName = "HomeV2TopStories";
